/**
 * Site cart drawer.
 *
 * Rendered once and portaled onto <body> — deliberately NOT inside the navbar,
 * which fades to opacity:0 / pointer-events:none on scroll-down. As a body-level
 * overlay the panel can always open and stay interactive, whatever the page
 * scroll position.
 *
 * Motion: the backdrop blurs in, the panel slides from the right, and the items
 * stagger up. All window / gsap access lives inside effects (SSR-safe); the
 * portal itself only mounts after hydration.
 */
import type { FC } from 'react'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'

import {
    ArrowRight,
    Minus,
    Plus,
    ShoppingBag,
    Trash2,
    X
} from 'lucide-react'

import { gsap } from '@/lib/gsap'
import { useCart } from '@/lib/cart'
import { formatPrice } from '@/lib/format'

export const CartDrawer: FC = () => {
    const cart = useCart()

    const [mounted, setMounted] = useState(false)
    const overlayRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => setMounted(true), [])

    /* Open / close: animate the backdrop + panel and lock body scroll. */
    useEffect(() => {
        const overlay = overlayRef.current
        const panel = panelRef.current
        if (!overlay || !panel) return

        if (cart.isOpen) {
            document.body.style.overflow = 'hidden'
            gsap.set(overlay, { display: 'flex' })
            gsap.to(overlay, { autoAlpha: 1, duration: 0.3, ease: 'power1.out' })
            gsap.fromTo(
                panel,
                { xPercent: 100 },
                { xPercent: 0, duration: 0.55, ease: 'power3.out' }
            )

            const staggered = panel.querySelectorAll('[data-cart-stagger]')
            gsap.fromTo(
                staggered,
                { opacity: 0, y: 20 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    delay: 0.15,
                    stagger: 0.07,
                    ease: 'power2.out'
                }
            )
        } else {
            document.body.style.overflow = ''
            gsap.to(panel, { xPercent: 100, duration: 0.4, ease: 'power2.in' })
            gsap.to(overlay, {
                autoAlpha: 0,
                duration: 0.3,
                delay: 0.05,
                onComplete: () => gsap.set(overlay, { display: 'none' })
            })
        }
    }, [cart.isOpen])

    /* Always release the scroll lock if this unmounts while open. */
    useEffect(() => {
        return () => {
            document.body.style.overflow = ''
        }
    }, [])

    /* Close on Escape while open. */
    useEffect(() => {
        if (!cart.isOpen) return

        const onKey = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') cart.closeCart()
        }

        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [cart.isOpen, cart])

    if (!mounted) return null

    const items = cart.hydrated ? cart.items : []
    const hasItems = items.length > 0

    return createPortal(
        <div
            ref={overlayRef}
            role='presentation'
            className='mcart-overlay'
            onClick={() => cart.closeCart()}
        >
            <aside
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-label='Your cart'
                className='mcart-panel'
                onClick={(event) => event.stopPropagation()}
            >
                <header className='mcart-header'>
                    <div className='mcart-heading-group'>
                        <h2 className='mcart-title'>Your Cart</h2>
                        <span className='mcart-subtitle'>
                            {hasItems ? `${cart.count} ${cart.count === 1 ? 'item' : 'items'}` : 'Empty'}
                        </span>
                    </div>
                    <button
                        type='button'
                        className='mcart-close'
                        aria-label='Close cart'
                        onClick={() => cart.closeCart()}
                    >
                        <X className='size-5' aria-hidden='true' />
                    </button>
                </header>

                {hasItems ? (
                    <>
                        <div className='mcart-body'>
                            {items.map((item) => (
                                <article key={item.variantId} data-cart-stagger className='mcart-item'>
                                    <div className='mcart-item-image-holder'>
                                        <img src={item.image} alt={item.name} loading='lazy' className='mcart-item-image' />
                                    </div>
                                    <div className='mcart-item-info'>
                                        <div className='mcart-item-top'>
                                            <h3 className='mcart-item-name'>{item.name}</h3>
                                            <span className='mcart-item-price'>
                                                {formatPrice(item.price * item.quantity)}
                                            </span>
                                        </div>

                                        {(item.option1 || item.option2) && (
                                            <div className='mcart-item-options'>
                                                {[item.option1, item.option2].filter(Boolean).join(' / ')}
                                            </div>
                                        )}

                                        <div className='mcart-item-controls'>
                                            <div className='mcart-qty'>
                                                <button
                                                    type='button'
                                                    className='mcart-qty-btn'
                                                    aria-label={`Decrease quantity of ${item.name}`}
                                                    onClick={() => cart.updateQuantity(item.variantId, item.quantity - 1)}
                                                >
                                                    <Minus className='size-4' aria-hidden='true' />
                                                </button>
                                                <span className='mcart-qty-value'>{item.quantity}</span>
                                                <button
                                                    type='button'
                                                    className='mcart-qty-btn'
                                                    aria-label={`Increase quantity of ${item.name}`}
                                                    onClick={() => cart.updateQuantity(item.variantId, item.quantity + 1)}
                                                >
                                                    <Plus className='size-4' aria-hidden='true' />
                                                </button>
                                            </div>

                                            <button
                                                type='button'
                                                className='mcart-remove'
                                                onClick={() => cart.removeItem(item.variantId)}
                                            >
                                                <Trash2 className='size-4' aria-hidden='true' />
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <footer data-cart-stagger className='mcart-footer'>
                            <div className='mcart-subtotal'>
                                <span className='mcart-subtotal-label'>Subtotal</span>
                                <span className='mcart-subtotal-value'>{formatPrice(cart.subtotal)}</span>
                            </div>
                            <p className='mcart-tax-note'>Shipping and taxes calculated at checkout.</p>
                            <button type='button' className='mcart-checkout' onClick={() => cart.closeCart()}>
                                Proceed to checkout
                                <ArrowRight className='size-4 mcart-checkout-icon' aria-hidden='true' />
                            </button>
                        </footer>
                    </>
                ) : (
                    <div data-cart-stagger className='mcart-empty'>
                        <span className='mcart-empty-icon'>
                            <ShoppingBag className='size-8' aria-hidden='true' />
                        </span>
                        <h3 className='mcart-empty-title'>Your cart is empty</h3>
                        <p className='mcart-empty-text'>
                            Nothing here yet. Explore the collection and find the piece that is meant for you.
                        </p>
                        <Link href='/products' className='mcart-empty-button' onClick={() => cart.closeCart()}>
                            Continue shopping
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </Link>
                    </div>
                )}
            </aside>
        </div>,
        document.body
    )
}
